import { VibeshipConfig } from './types';

/**
 * Default configuration for Vibeship DevTools
 */
export const defaultConfig: VibeshipConfig = {
  // File scanning
  scanPaths: ['./docs', './README.md'],
  include: ['**/*.md', '**/*.mdx'],
  exclude: ['**/node_modules/**', '**/.next/**', '**/.git/**', '**/dist/**', '**/build/**'],

  // Features
  features: {
    tasks: true,
    ai: false,
    realtime: true,
    commandPalette: true,
    fileEditing: false,
    markdownPreview: true,
  },

  // Security
  security: {
    authentication: false,
    rateLimit: true,
    cors: true,
    blockedPatterns: [
      '**/.env*',
      '**/*.key',
      '**/*.pem',
      '**/*.p12',
      '**/*.pfx',
      '**/secrets/**',
      '**/credentials/**',
    ],
  },

  // Task patterns
  taskPatterns: {
    todo: true,
    fixme: true,
    hack: true,
    note: true,
  },

  // UI configuration
  ui: {
    theme: 'system',
    position: 'bottom-right',
    defaultSize: {
      width: '400px',
      height: '600px',
    },
    hotkey: 'cmd+shift+k',
    showInProduction: false,
  },

  // API configuration
  api: {
    basePath: '/api/vibeship',
    version: 'v1',
    documentation: true,
    timeout: 30000, // 30 seconds
  },

  // Cache configuration
  cache: {
    enabled: true,
    directory: '.vibeship/cache',
    maxSize: 52428800, // 50MB
    ttl: 3600000, // 1 hour
    strategy: 'lru',
  },
};

/**
 * Development-specific configuration overrides
 */
export const developmentConfig: Partial<VibeshipConfig> = {
  features: {
    ...defaultConfig.features,
    fileEditing: true,
  },
  security: {
    ...defaultConfig.security,
    authentication: false,
    rateLimitConfig: {
      windowMs: 60000,
      maxRequests: 1000, // More lenient in development
    },
  },
  ui: {
    ...defaultConfig.ui,
    showInProduction: true, // Allow in dev mode
  },
  cache: {
    ...defaultConfig.cache,
    ttl: 300000, // 5 minutes in dev
  },
};

/**
 * Production-specific configuration overrides
 */
export const productionConfig: Partial<VibeshipConfig> = {
  features: {
    ...defaultConfig.features,
    fileEditing: false, // Never allow file editing in production
  },
  security: {
    ...defaultConfig.security,
    authentication: true,
    rateLimit: true,
    rateLimitConfig: {
      windowMs: 60000,
      maxRequests: 100,
      tiers: {
        anonymous: { windowMs: 60000, maxRequests: 20 },
        authenticated: { windowMs: 60000, maxRequests: 100 },
        premium: { windowMs: 60000, maxRequests: 1000 },
      },
    },
    corsConfig: {
      allowedOrigins: (origin: string) => {
        // In production, validate against allowed domains
        const allowedDomains = process.env.ALLOWED_DOMAINS?.split(',') || [];
        return allowedDomains.some(domain => origin.includes(domain));
      },
      credentials: true,
      maxAge: 86400,
    },
  },
  ui: {
    ...defaultConfig.ui,
    showInProduction: false,
  },
  cache: {
    ...defaultConfig.cache,
    maxSize: 104857600, // 100MB in production
    ttl: 7200000, // 2 hours in production
  },
};

/**
 * Test-specific configuration overrides
 */
export const testConfig: Partial<VibeshipConfig> = {
  scanPaths: ['./test-docs'],
  security: {
    ...defaultConfig.security,
    authentication: false,
    rateLimit: false,
  },
  cache: {
    ...defaultConfig.cache,
    enabled: false, // Disable cache in tests
  },
  ui: {
    ...defaultConfig.ui,
    showInProduction: true,
  },
};

/**
 * Get environment-specific defaults
 */
export function getEnvironmentDefaults(): Partial<VibeshipConfig> {
  const env = process.env.NODE_ENV || 'development';
  
  switch (env) {
    case 'production':
      return productionConfig;
    case 'test':
      return testConfig;
    case 'development':
    default:
      return developmentConfig;
  }
}